// Click a chart, get that one chart big.
//
// A track in the stack is 76 px tall and carries four models, an ensemble band,
// a limit, a buoy and the map field's own trace. That is the right density for
// "what shape, and is it over the line", and the wrong density for reading a
// particular hour off it. So the plot gets a room of its own on demand, with
// the height to separate the traces, the width to print the readout without
// wrapping it into a paragraph, and the same Source line underneath.
//
// It reuses the console's one overlay idiom — ui/opsgate.ts's confirm dialog: a
// centered `.block` card on a dimmed backdrop, Escape and click-outside to
// close, focus handed back where it came from. A second overlay style would be
// a second set of habits to learn on a bridge.
//
// The chart inside is built by the CALLER'S OWN builder — the same closure
// main.ts hands to mountChart. That is what makes this cheap: nothing is
// refetched, nothing upstream is rebuilt, and the modal chart is a second
// reader of the shared cursor, so scrubbing works in here exactly as it does
// out there. Closing destroys only what opening created.

import type { Chart } from "./chart.js";
import { attachReadout } from "./readout.js";

export interface ExpandOptions {
  title: string;
  /** The block's mono subtitle, if it has one. */
  sub: string;
  /** The block's "Source: …" line, carried through verbatim. */
  source: string;
  /** The block's own chart builder. Called once, with the modal's canvas. */
  build: (canvas: HTMLCanvasElement) => Chart | null;
  /** Focus goes back here on close — the element that was clicked. */
  returnFocusTo: HTMLElement | null;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

// One at a time. Two stacked chart modals would put two readouts on one cursor
// with no way to tell which chart either belonged to.
let closeOpen: (() => void) | null = null;

/** True while a chart modal is up (the caller uses this to ignore a second click). */
export function chartModalOpen(): boolean {
  return closeOpen !== null;
}

export function openChartModal(opts: ExpandOptions): void {
  closeOpen?.();

  // On document.body, deliberately. The night dimmer (map/dimmer.ts) puts a CSS
  // filter on MapLibre's canvas container, and a `filter` makes that container a
  // containing block for fixed positioning — a modal parented anywhere inside it
  // would be laid out against the map instead of the viewport, and dimmed twice.
  const backdrop = el("div", "cmodal-backdrop");
  const card = el("section", "cmodal block");
  card.setAttribute("role", "dialog");
  card.setAttribute("aria-modal", "true");
  card.setAttribute("aria-label", opts.title);

  const bh = el("div", "bh");
  bh.append(el("span", "bt", opts.title));
  if (opts.sub.length > 0) bh.append(el("span", "bsub", opts.sub));
  const closeBtn = el("button", "cmodal-x", "✕");
  closeBtn.type = "button";
  closeBtn.setAttribute("aria-label", "Close");
  bh.append(closeBtn);
  card.append(bh);

  const readout = el("div", "creadout");
  const wrap = el("div", "chart-wrap");
  const canvas = document.createElement("canvas");
  wrap.append(canvas);
  const src = el("div", "bsrc source", opts.source);
  card.append(readout, wrap, src);
  backdrop.append(card);
  document.body.append(backdrop);

  // Built AFTER the card is in the document: the chart measures its canvas on
  // construction, and a detached element measures zero.
  const chart = opts.build(canvas);
  const unsubReadout = chart !== null ? attachReadout(chart, readout) : null;

  const close = (): void => {
    document.removeEventListener("keydown", onKey, true);
    unsubReadout?.();
    // The whole point of the destroy() discipline: no orphan ResizeObserver, no
    // canvas still subscribed to the cursor after its card is gone.
    chart?.destroy();
    backdrop.remove();
    closeOpen = null;
    opts.returnFocusTo?.focus();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === "Escape") {
      e.stopPropagation();
      close();
    }
  };
  // Capture, so Escape closes the chart modal before anything below it (the help
  // popover binds Escape on the document too) decides it was meant for them.
  document.addEventListener("keydown", onKey, true);
  closeBtn.addEventListener("click", close);
  backdrop.addEventListener("click", (e) => {
    if (e.target === backdrop) close();
  });
  closeOpen = close;
  closeBtn.focus();
}
